import { useCallback, useMemo, useState } from 'react'

import type { Map as ImmutableMap } from 'immutable'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'

import {
  basketDateChange,
  basketRecipePositionsInitialise,
  basketRecipesInitialise,
  basketSlotChange,
} from 'actions/basket'
import { fetchData } from 'routes/Menu/fetchData'
import { getDisabledSlotDates } from 'routes/Menu/selectors/boxSummary'
import { getIsAuthenticated } from 'selectors/auth'
import { getBasketDate, getBasketRecipesCount, getBasketSlotId } from 'selectors/basket'
import { getBoxSummaryDeliveryDays } from 'selectors/root'
import { getUserOrders } from 'selectors/user'
import { addDisabledSlotIds, getDeliveryDaysAndSlots } from 'utils/deliverySlotHelper'
import type { UserOrder } from 'utils/types/order'

import { trackEditOrderDetailsDeliveryDayCompleted } from './tracking'
import { useCurrentMenuPeriod } from './useCurrentMenuPeriod'

export type SlotOption = {
  // Composite key — slotId alone is not unique across days (slot-tier IDs are
  // shared between dates), so we identify each option by `${date}|${slotId}`.
  optionId: string
  slotId: string
  date: string
  label: string
}

export type UseEditDeliveryDay = {
  availableSlots: SlotOption[]
  currentOptionId: string
  selectedOptionId: string
  setSelectedOptionId: (optionId: string) => void
  basketWillBeEmptied: boolean
  menuWillBeUpdated: boolean
  canSave: boolean
  confirm: () => void
}

const buildOptionId = (date: string, slotId: string): string => `${date}|${slotId}`

// Returns the slot's `whenCutoff` — used to resolve its menu period.
const getSlotCutoffAt = (
  boxSummaryDeliveryDays: ImmutableMap<string, any>,
  date: string,
  slotId: string,
): string | undefined => {
  if (!date || !slotId) return undefined
  const day = boxSummaryDeliveryDays.get(date)
  if (!day) return undefined
  const slots = day.get('slots')
  const match = slots?.find((slot: ImmutableMap<string, any>) => slot.get('id') === slotId)

  return match?.get('whenCutoff')
}

type BuildAvailableSlotsInputs = {
  currentDate: string
  currentSlotId: string
  disabledSlots: ReturnType<typeof getDisabledSlotDates>
  isAuthenticated: boolean
  userOrders: ReturnType<typeof getUserOrders>
  boxSummaryDeliveryDays: ReturnType<typeof getBoxSummaryDeliveryDays>
}

export const buildAvailableSlots = ({
  currentDate,
  currentSlotId,
  disabledSlots,
  isAuthenticated,
  userOrders,
  boxSummaryDeliveryDays,
}: BuildAvailableSlotsInputs): SlotOption[] => {
  const { slots, deliveryDays } = getDeliveryDaysAndSlots(currentDate, {
    disabledSlots,
    isAuthenticated,
    tempDate: currentDate,
    userOrders,
    tempSlotId: currentSlotId,
    deliveryDaysProps: addDisabledSlotIds(boxSummaryDeliveryDays),
  })

  // Exclude days with an existing order — basketSlotChange would repoint
  // the basket at it and bounce the user into the order-edit flow.
  const datesWithExistingOrders = new Set<string>(
    userOrders
      .toArray()
      .map((order: UserOrder) => (order.get('deliveryDate') ?? '').split(' ')[0])
      .filter((date: string) => !!date),
  )

  const options: SlotOption[] = []
  ;(deliveryDays ?? []).forEach((day) => {
    if (day.disabled) return
    if (day.date !== currentDate && datesWithExistingOrders.has(day.date)) return
    ;(slots[day.date] ?? []).forEach((slot) => {
      if (slot.disabled) return
      options.push({
        optionId: buildOptionId(day.date, slot.value),
        slotId: slot.value,
        date: day.date,
        label: `${moment(day.date).format('ddd D MMM')}, ${slot.label.trim()}`,
      })
    })
  })

  return options
}

export const useEditDeliveryDay = (): UseEditDeliveryDay => {
  const dispatch = useDispatch()
  const boxSummaryDeliveryDays = useSelector(getBoxSummaryDeliveryDays)
  const disabledSlots = useSelector(getDisabledSlotDates)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const userOrders = useSelector(getUserOrders)
  const currentSlotId = (useSelector(getBasketSlotId) as string | undefined) ?? ''
  const currentDate = (useSelector(getBasketDate) as string | undefined) ?? ''
  const basketRecipesCount = useSelector(getBasketRecipesCount)

  const availableSlots: SlotOption[] = useMemo(
    () =>
      buildAvailableSlots({
        currentDate,
        currentSlotId,
        disabledSlots,
        isAuthenticated,
        userOrders,
        boxSummaryDeliveryDays,
      }),
    [
      boxSummaryDeliveryDays,
      currentDate,
      currentSlotId,
      disabledSlots,
      isAuthenticated,
      userOrders,
    ],
  )

  const currentOptionId = buildOptionId(currentDate, currentSlotId)

  const [selectedOptionId, setSelectedOptionId] = useState<string>(currentOptionId)

  const hasChanged = selectedOptionId !== currentOptionId
  const selectedSlot = availableSlots.find((s) => s.optionId === selectedOptionId)

  const currentSlotCutoff = getSlotCutoffAt(boxSummaryDeliveryDays, currentDate, currentSlotId)
  const selectedSlotCutoff = selectedSlot
    ? getSlotCutoffAt(boxSummaryDeliveryDays, selectedSlot.date, selectedSlot.slotId)
    : undefined

  // Same menu = same active-period id (multiple dates can share one ordering window).
  const { getPeriodIdForCutoff } = useCurrentMenuPeriod()
  const currentPeriodId = getPeriodIdForCutoff(currentSlotCutoff)
  const selectedPeriodId = getPeriodIdForCutoff(selectedSlotCutoff)
  const sameMenuPeriod =
    !!currentPeriodId && !!selectedPeriodId && currentPeriodId === selectedPeriodId
  const menuWillChange = hasChanged && !!selectedSlot && !sameMenuPeriod

  const confirm = useCallback(() => {
    if (!hasChanged) return
    if (!selectedSlot) return

    if (basketRecipesCount > 0) {
      dispatch(basketRecipesInitialise({}))
      dispatch(basketRecipePositionsInitialise([]))
    }
    dispatch(basketDateChange(selectedSlot.date))
    dispatch(basketSlotChange(selectedSlot.slotId))
    // basketSlotChange doesn't refetch the menu — mirror basketRestorePreviousDate.
    dispatch(
      fetchData({ query: {}, params: {} }, true, undefined, {
        addRecipe: () => {},
      }),
    )
    trackEditOrderDetailsDeliveryDayCompleted({
      previous_delivery_date: currentDate,
      updated_delivery_date: selectedSlot.date,
    })
  }, [dispatch, hasChanged, selectedSlot, basketRecipesCount, currentDate])

  return {
    availableSlots,
    currentOptionId,
    selectedOptionId,
    setSelectedOptionId,
    basketWillBeEmptied: hasChanged && basketRecipesCount > 0,
    menuWillBeUpdated: menuWillChange && basketRecipesCount === 0,
    canSave: hasChanged,
    confirm,
  }
}
